import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { motion } from 'framer-motion';
import {
  Plus,
  Send,
  Sparkles,
  X,
  CreditCard
} from 'lucide-react';

// Message Modal Component
export function MessageModal({
  show,
  selectedTraveler,
  messageSubject,
  setMessageSubject,
  messageText,
  setMessageText,
  onClose,
  onSend
}) {
  if (!show || !selectedTraveler) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-primary">
              Message {selectedTraveler.name}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {selectedTraveler.tripInterest && (
            <div className="mb-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>{selectedTraveler.tripInterest}</strong> • {selectedTraveler.dates}
              </p>
              {selectedTraveler.bio && (
                <p className="text-sm text-muted-foreground mt-1">"{selectedTraveler.bio}"</p>
              )}
            </div>
          )}
          <div className="space-y-3 mb-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Subject (optional)</Label>
              <Input
                placeholder="e.g., Trip coordination, Question about..."
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                data-testid="message-subject"
              />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Message</Label>
              <Textarea
                placeholder="Write your message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={4}
                data-testid="message-textarea"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={onSend}
              className="bg-secondary hover:bg-secondary/90"
              disabled={!messageText.trim()}
              data-testid="send-message-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Message
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Inbox/Conversation Modal Component
export function InboxModal({
  show,
  user,
  activeConversation,
  conversationMessages,
  messageText,
  setMessageText,
  onClose,
  onSend
}) {
  if (!show || !activeConversation) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
      >
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-secondary/20 text-secondary">
                {activeConversation.partner_name?.split(' ').map(n => n[0]).join('') || '?'}
              </AvatarFallback>
            </Avatar>
            <h3 className="font-heading font-semibold text-primary">
              {activeConversation.partner_name}
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: '300px' }}>
          {conversationMessages.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No messages yet. Start the conversation!</p>
          ) : (
            conversationMessages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.sender_id === user?.id
                      ? 'bg-secondary text-white'
                      : 'bg-muted'
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onSend()}
              className="flex-1"
              data-testid="inbox-message-input"
            />
            <Button 
              onClick={onSend}
              className="bg-secondary hover:bg-secondary/90"
              disabled={!messageText.trim()}
              data-testid="inbox-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Add-ons Modal Component
export function AddOnsModal({
  show,
  selectedBooking,
  loadingAddOns,
  modalAvailableAddOns,
  modalSelectedAddOns,
  toggleAddOnSelection,
  onClose,
  onConfirm,
  addingAddOns,
  processingPayment
}) {
  if (!show || !selectedBooking) return null;

  const existingIds = (selectedBooking.selected_add_ons || []).map(a => a.id);
  const newAddOns = modalSelectedAddOns.filter(a => !existingIds.includes(a.id));
  const totalCost = newAddOns.reduce((sum, a) => sum + (a.price || 0), 0);
  const hasNewAddOns = newAddOns.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Add Excursions</h2>
              <p className="text-white/80 text-sm mt-1">{selectedBooking.trip_title}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Already Booked */}
          {selectedBooking.selected_add_ons && selectedBooking.selected_add_ons.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-green-500" />
                Already Booked
              </h3>
              <div className="space-y-2">
                {selectedBooking.selected_add_ons.map(addon => (
                  <div key={addon.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-sm font-medium text-green-800">{addon.name}</span>
                    <Badge className="bg-green-500">${addon.price}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Add-ons */}
          <div>
            <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <Plus className="h-4 w-4 text-secondary" />
              Available Excursions
            </h3>
            
            {loadingAddOns ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto"></div>
                <p className="text-sm text-muted-foreground mt-2">Loading excursions...</p>
              </div>
            ) : modalAvailableAddOns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Sparkles className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No additional excursions available</p>
                <p className="text-sm">You've already booked all available options!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {modalAvailableAddOns.map(addon => {
                  const isSelected = modalSelectedAddOns.some(a => a.id === addon.id);
                  return (
                    <div
                      key={addon.id}
                      onClick={() => toggleAddOnSelection(addon)}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-secondary bg-secondary/5' 
                          : 'border-gray-200 hover:border-secondary/50'
                      }`}
                      data-testid={`modal-addon-${addon.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'border-secondary bg-secondary' : 'border-gray-300'
                        }`}>
                          {isSelected && <span className="text-white text-xs">✓</span>}
                        </div>
                        <div>
                          <p className="font-medium text-primary">{addon.name}</p>
                          {addon.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{addon.description}</p>
                          )}
                        </div>
                      </div>
                      <span className={`font-bold ${addon.price === 0 ? 'text-green-600' : 'text-secondary'}`}>
                        {addon.price === 0 ? 'FREE' : `$${addon.price}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="border-t p-4 bg-gray-50">
          {hasNewAddOns && (
            <div className="mb-3 p-3 bg-secondary/10 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {newAddOns.length} new excursion(s) selected
                </span>
                <span className="font-bold text-secondary">
                  {totalCost === 0 ? 'FREE' : `+$${totalCost.toLocaleString()}`}
                </span>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={processingPayment}
            >
              Cancel
            </Button>
            <Button
              className={`flex-1 ${totalCost > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-secondary hover:bg-secondary/90'}`}
              onClick={onConfirm}
              disabled={addingAddOns || processingPayment || !hasNewAddOns}
              data-testid="confirm-add-excursions"
            >
              {processingPayment ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : addingAddOns ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : totalCost > 0 ? (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay ${totalCost.toLocaleString()}
                </>
              ) : (
                'Add to Booking'
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Payment Processing Overlay
export function PaymentProcessingOverlay({ show }) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-white/90 flex items-center justify-center z-[100]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent mx-auto mb-4"></div>
        <p className="text-lg font-medium text-primary">Redirecting to payment...</p>
        <p className="text-sm text-muted-foreground mt-2">Please wait while we connect you to Stripe</p>
      </div>
    </div>
  );
}
